import { HeatmapInts2D } from '@aipb/gulliver/castor/model';
import { SearchItem } from '@aipb/gulliver/catalog/model';
import { UiOpenLayersModule } from '@aipb/shared/ui/open-layers';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Map, MapBrowserEvent, View } from 'ol';
import { Control } from 'ol/control';
import { Coordinate } from 'ol/coordinate';
import { Interaction } from 'ol/interaction';
import BaseLayer from 'ol/layer/Base';
import { fromLonLat, transformExtent } from 'ol/proj';
import { Observable } from 'rxjs';
import { SearchMapService } from './search-map.service';
import { getIntersection } from 'ol/extent';

@UntilDestroy()
@Component({
  selector: 'cat-search-map',
  templateUrl: './search-map.component.html',
  styleUrls: ['./search-map.component.scss'],
  providers: [SearchMapService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, UiOpenLayersModule]
})
export class SearchMapComponent implements OnInit, OnDestroy {
  @Input() searchItem: SearchItem;
  @Input() disableZoomOut = false;
  @Input() showLayerSwitch = true;

  @Output() moveEnd: EventEmitter<void> = new EventEmitter<void>();

  private searchMapService = inject(SearchMapService);

  public layers$: Observable<BaseLayer[]> = this.searchMapService.layers$;
  public view$: Observable<View> = this.searchMapService.view$;
  public controls$: Observable<Control[]> = this.searchMapService.controls$;
  public interactions$: Observable<Interaction[]> = this.searchMapService.interactions$;

  private _extent: number[] = null;

  @Input() set extent(value: number[]) {
    this._extent = value;
    this.searchMapService.initExtent(this._extent);
    this.searchMapService.fitToExtent();
  }

  private _center: Coordinate = [0, 0];

  @Input() set center(value: Coordinate) {
    this._center = value;
    this.searchMapService.setCenter(this._center);
  }

  private _zoom = 2;

  @Input() set zoom(value: number) {
    this._zoom = value;
    this.searchMapService.setZoom(this._zoom);
  }

  private _points: number[][] = [];

  @Input() set points(value: number[][]) {
    this._points = value;
    this.searchMapService.initPoints(this._points);
  }

  private _heatmap: HeatmapInts2D;

  @Input() set heatmap(value: HeatmapInts2D) {
    this._heatmap = value;
    this.searchMapService.initHeatmap(this._heatmap);
    this.searchMapService.initRecords(this._heatmap);
    this.searchMapService.initGrid(this._heatmap);
  }

  ngOnInit() {
    this.initialize();
  }

  updateMap(): void {
    this.searchMapService.map.updateSize();
  }

  onMapReady(map: Map): void {
    this.searchMapService.updateMap(map);
  }

  onMapMoveEnd(): void {
    this.moveEnd.emit();
  }

  public getCoordinates(): number[] {
    const view = this.searchMapService.getView();

    if (!view) return [0, 0];

    const viewExtent = view.calculateExtent(this.searchMapService.map.getSize());

    if (!this._extent) {
      return transformExtent(viewExtent, 'EPSG:3857', 'EPSG:4326');
    }

    const searchExtent = [
      ...fromLonLat([this._extent[1], this._extent[0]]),
      ...fromLonLat([this._extent[3], this._extent[2]])
    ];

    return transformExtent(getIntersection(viewExtent, searchExtent), 'EPSG:3857', 'EPSG:4326');
  }

  public getSize(): number[] {
    return this.searchMapService.getSize();
  }

  public getZoom(): number {
    return this.searchMapService.getZoom();
  }

  public getMinZoom(): number {
    return this.searchMapService.getMinZoom();
  }

  public getMaxZoom(): number {
    return this.searchMapService.getMaxZoom();
  }

  public getCenter(): number[] {
    return this.searchMapService.getCenter();
  }

  onMapClick(evt: MapBrowserEvent<never>) {
    this.searchMapService.onMapClick(evt);
  }

  ngOnDestroy() {
    this.searchMapService.destroy();
  }

  private initialize(): void {
    this.searchMapService.init(this._zoom, this._extent, this._center, this.showLayerSwitch);
    this.searchMapService.initPoints(this._points);
    this.searchMapService.initExtent(this._extent);
    this.searchMapService.initHeatmap(this._heatmap);
    this.searchMapService.initRecords(this._heatmap);
    this.searchMapService.initGrid(this._heatmap);
  }
}
